import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import styles from './ProtectedContent.module.css';
import Loading from '../../common/HousGlass Loader/Loading';

// The main protected content component
const ProtectedContent = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const hasCheckedAccess = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Skip if we've already checked access
    if (hasCheckedAccess.current) return;
    
    hasCheckedAccess.current = true;
    
    // Initialize Fingerprint.js and get the visitor identifier
    const getBrowserId = async () => {
      // Initialize an agent at application startup.
      const fpPromise = FingerprintJS.load();
      
      // Get the visitor identifier when you need it.
      const fp = await fpPromise;
      const result = await fp.get();
      
      // Return the visitor identifier
      return result.visitorId;
    };

    const checkAccess = async () => {
      // Flag to prevent state updates after unmounting
      let isMounted = true;
      
      try {
        setIsLoading(true);
        
        // Get browser fingerprint for visitor tracking
        const browserId = await getBrowserId();
        
        // Check if component is still mounted before proceeding
        if (!isMounted) return;
        
        // Make request to access check endpoint
        const response = await axios.get('https://sunokahaniyan.com/api/api/auth/check-access', {
          headers: {
            'X-Browser-ID': browserId
          },
          withCredentials: true // Important for sending cookies with the request
        });
        
        // Check if component is still mounted before updating state
        if (isMounted) {
          console.log(response);
          setIsAuthorized(true);
        }
      } catch (error) {
        if (!isMounted) return;
        
        console.error('Access denied:', error.response?.data);
        
        // Handle redirection based on server response
        if (error.response?.status === 403 && error.response?.data?.redirectToLogin) {
          // User has exceeded guest play limit
          navigate('/limit-over', {
            state: {
              from: window.location.pathname,
              message: error.response?.data?.error || 'Guest play limit reached. Please log in to continue.'
            }
          });
        } else if (error.response?.status === 401) {
          // User is not authenticated
          navigate('/signin', {
            state: {
              from: window.location.pathname,
              message: 'Please log in to access this content.'
            }
          });
        } else {
          // Other errors - could redirect to an error page
          navigate('/signin', {
            state: {
              message: 'Something went wrong. Please try again later.'
            }
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
      
      // Cleanup function
      return () => {
        isMounted = false;
      };
    };

    checkAccess();
  }, [navigate]);

  // Show loading state
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Loading/>
      </div>
    );
  }

  // If authorized, render the children components
  return isAuthorized ? children : null;
};

export default ProtectedContent;
